/**
 * Still-ahead day-group heading copy for Discover list timeline (WEB-671,
 * WEB-924).
 *
 * The weekday/date fragment is city-local. `?date=tomorrow` must print
 * calendar tomorrow in `timeZone`, not the wall-clock of `now` — that
 * painted today's weekday on a tomorrow list.
 */
import { formatEventTime } from '@loci/types';
import { addLocalDays } from '@loci/validation';

import { formatCount } from './formatCount';
import type { DateValue } from './url-state';

export interface TimelineGroupCopy {
  readonly title: string;
  readonly detail: string;
}

const weekdayDayMonthFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getWeekdayDayMonthFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = weekdayDayMonthFormatterCache.get(timeZone);
  if (cached) return cached;
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone,
    });
  } catch {
    formatter = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
  }
  weekdayDayMonthFormatterCache.set(timeZone, formatter);
  return formatter;
}

/** `Wed 29 Apr` in `timeZone` — the Still-ahead head's calendar fragment. */
export function formatShortWeekday(instant: Date, timeZone: string): string {
  const parts = getWeekdayDayMonthFormatter(timeZone).formatToParts(instant);
  const weekday = parts.find((part) => part.type === 'weekday')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  return [weekday, day, month].filter(Boolean).join(' ');
}

function todayLocalDate(cityTimezone: string, now: Date): string {
  return formatEventTime(now, cityTimezone).localDate;
}

/**
 * UTC instant for `YYYY-MM-DDTHH:mm:ss` interpreted in `timeZone`.
 * Noon local is the heading clock: some zones skip 00:00 on DST spring-forward.
 */
function fromZonedLocalIso(localIso: string, timeZone: string): Date {
  const utcAsIf = new Date(`${localIso}Z`);
  if (Number.isNaN(utcAsIf.getTime())) return utcAsIf;
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
  } catch {
    return utcAsIf;
  }
  const parts = formatter.formatToParts(utcAsIf);
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number.parseInt(parts.find((part) => part.type === type)?.value ?? '0', 10);
  const asLocalMs = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour'),
    read('minute'),
    read('second'),
  );
  return new Date(utcAsIf.getTime() - (asLocalMs - utcAsIf.getTime()));
}

/**
 * Instant whose city-local calendar date is the Still-ahead heading date.
 *
 * `tonight` and the generic fallback stay on `now`. `tomorrow` is the next
 * city-local calendar day (WEB-924).
 */
export function stillAheadHeadingInstant(
  date: DateValue | undefined,
  now: Date,
  timeZone: string,
): Date {
  if (date !== 'tomorrow') return now;
  const tomorrow = addLocalDays(todayLocalDate(timeZone, now), 1);
  return fromZonedLocalIso(`${tomorrow}T12:00:00`, timeZone);
}

export function buildStillAheadCopy(
  date: DateValue | undefined,
  now: Date | null,
  count: number,
  remainder: number,
  timeZone: string,
): TimelineGroupCopy {
  const headingInstant = now ? stillAheadHeadingInstant(date, now, timeZone) : null;
  const day = headingInstant ? formatShortWeekday(headingInstant, timeZone) : null;
  const scope =
    date === 'tonight'
      ? 'Still ahead tonight'
      : date === 'tomorrow'
        ? 'Still ahead tomorrow'
        : 'Still ahead';
  const title = day ? `${scope} · ${day}` : scope;
  const matched = `${formatCount(count)} matched`;
  const more = remainder > 0 ? ` · ${formatCount(remainder)} more in range` : '';
  return { title, detail: `${matched}${more}` };
}
